import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight, LogIn, LayoutDashboard, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ChangePasswordDialog } from "@/components/auth/ChangePasswordDialog";

const links = [
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/projects", label: "Projects" },
  { to: "/gallery", label: "Gallery" },
  { to: "/blog", label: "Insights" },
  { to: "/track", label: "Track Project" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="glass-header fixed top-0 left-0 right-0 z-50" data-testid="main-navbar">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
          <img src="/ogk-logo.png" alt="OGK logo" className="w-10 h-10 object-contain" />
          <span className="hidden sm:block leading-none">
            <span className="block font-heading font-semibold text-white text-sm tracking-wide uppercase">OGK Infra</span>
            <span className="block text-[10px] text-silver tracking-[0.3em] uppercase mt-1">& Developers</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 shrink-0">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] uppercase tracking-[0.12em] font-medium whitespace-nowrap transition-colors duration-200 ${isActive ? "text-ember" : "text-silver hover:text-white"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4 whitespace-nowrap shrink-0">
          {user && user.role === "admin" ? (
            <button onClick={() => navigate("/admin")} data-testid="nav-erp-dashboard-btn"
              className="flex items-center gap-2 text-[13px] uppercase tracking-[0.12em] text-silver hover:text-white transition-colors duration-200">
              <LayoutDashboard size={15} /> ERP
            </button>
          ) : user ? (
            <>
              <button onClick={() => navigate(user.role === "employee" || user.role === "supervisor" ? "/employee" : user.role === "vendor" ? "/vendor" : user.role === "accountant" ? "/accounts" : "/portal")} data-testid="nav-my-portal-btn"
                className="flex items-center gap-2 text-[13px] uppercase tracking-[0.12em] text-silver hover:text-white transition-colors duration-200">
                <LayoutDashboard size={15} /> My Portal
              </button>
              <button onClick={() => setPwOpen(true)} data-testid="nav-change-password-btn" title="Change password"
                className="text-silver hover:text-ember transition-colors duration-200">
                <KeyRound size={15} />
              </button>
            </>
          ) : (            <button onClick={() => navigate("/login")} data-testid="nav-erp-login-btn"
              className="flex items-center gap-2 text-[13px] uppercase tracking-[0.12em] text-silver hover:text-white transition-colors duration-200">
              <LogIn size={15} /> ERP Login
            </button>
          )}
          <Link to="/request-quote" data-testid="nav-request-quote-btn"
            className="group bg-ember text-white text-[13px] uppercase tracking-[0.12em] font-bold px-6 py-3 flex items-center gap-2 hover:bg-[#e64a19] transition-colors duration-200">
            Request a Quote <ArrowUpRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden glass-header border-t border-line px-6 py-8 space-y-5" data-testid="mobile-menu">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)}
              className="block text-lg uppercase tracking-wider text-white font-heading">{l.label}</NavLink>
          ))}
          <div className="pt-4 border-t border-line flex flex-col gap-4">
            <Link to="/login" onClick={() => setOpen(false)} className="text-silver uppercase text-sm tracking-widest">ERP Login</Link>
            <Link to="/request-quote" onClick={() => setOpen(false)}
              className="bg-ember text-white text-center uppercase text-sm tracking-widest font-bold px-6 py-4">Request a Quote</Link>
          </div>
        </div>
      )}
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </header>
  );
};
